import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useRefreshSession } from '../../lib/session';
import { errorText } from '../../i18n';
import { CenterScreen, Logo, Spinner } from '@vatman/ui';
import { AuthHero } from './AuthPage';
import './auth.css';

/** Landing pages for emailed links: verify email, magic sign-in, password reset. */
export function TokenPage({ kind }: { kind: 'verify' | 'magic' | 'reset' }) {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const refresh = useRefreshSession();
  const [state, setState] = useState<'working' | 'done' | 'error'>(kind === 'reset' ? 'done' : 'working');
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (kind === 'reset' || started.current) return;
    started.current = true;
    const path = kind === 'verify' ? '/auth/verify' : '/auth/magic/consume';
    api
      .post(path, { token, locale: i18n.language })
      .then(async () => {
        await refresh();
        if (kind === 'magic') navigate('/', { replace: true });
        else setState('done');
      })
      .catch((e) => {
        setError(errorText(e instanceof ApiError ? e.code : 'internal'));
        setState('error');
      });
  }, [kind, token, i18n.language, navigate, refresh]);

  const reset = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/auth/reset', { token, password });
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorText(err instanceof ApiError ? err.code : 'internal'));
    }
  };

  if (kind === 'reset') {
    return (
      <div className="auth">
        <AuthHero />
        <main className="auth-main">
          <form className="auth-card" onSubmit={reset}>
            <h2>{t('auth.resetTitle')}</h2>
            <label className="field">
              <span>{t('auth.password')}</span>
              <input className="input" type="password" minLength={8} required autoFocus value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
            </label>
            {error && <div className="error-text">{error}</div>}
            <button className="btn primary lg block">{t('auth.resetSave')}</button>
          </form>
        </main>
      </div>
    );
  }

  return (
    <CenterScreen>
      <Logo size={40} />
      {state === 'working' && (
        <>
          <Spinner />
          <div className="muted">{kind === 'verify' ? t('auth.verifying') : t('auth.signingIn')}</div>
        </>
      )}
      {state === 'done' && (
        <>
          <CheckCircle2 size={36} color="var(--success)" />
          <h2 style={{ margin: 0 }}>{t('auth.verified')}</h2>
          <button className="btn primary" onClick={() => navigate('/', { replace: true })}>
            {t('board.toDashboard')}
          </button>
        </>
      )}
      {state === 'error' && (
        <>
          <div className="error-text" style={{ fontSize: 'var(--fs-5)' }}>
            {error}
          </div>
          <button className="btn" onClick={() => navigate('/login')}>
            {t('auth.login')}
          </button>
        </>
      )}
    </CenterScreen>
  );
}
